// src/components/ProgramEdit.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { IoAdd, IoTrash, IoAddCircleOutline, IoArrowBack, IoBarbellOutline, IoSaveOutline } from 'react-icons/io5';
import { toast } from 'react-hot-toast';
import ExercisePicker from './ExercisePicker';

const DAY_LABELS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
const getAlphaDay = (n) => `Día ${String.fromCharCode(64 + ((n - 1) % 26) + 1)}`;
const getCalendarName = (order, trainingDays) => {
    const sorted = [...trainingDays].sort((a, b) => a - b);
    const dayNum = sorted[order - 1];
    return dayNum ? DAY_LABELS[dayNum - 1] : `Rutina ${order}`;
};

const ProgramEdit = () => {
    const { programId } = useParams();
    const navigate = useNavigate();

    // Estados del componente
    const [programName, setProgramName] = useState('');
    const [description, setDescription] = useState('');
    const [durationWeeks, setDurationWeeks] = useState(4);
    const [trainingDays, setTrainingDays] = useState([]);
    const [trainingMode, setTrainingMode] = useState('sequence'); // sequence | calendar
    const [routines, setRoutines] = useState([]);
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const [currentRoutineIndex, setCurrentRoutineIndex] = useState(null);
    const [currentGroupIndex, setCurrentGroupIndex] = useState(null);
    const [expandedRoutine, setExpandedRoutine] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchProgram = async () => {
            try {
                const { data } = await api.get(`/programs/${programId}`);
                setProgramName(data.name);
                setDescription(data.description || '');
                setDurationWeeks(data.duration_weeks || 4);
                setTrainingDays(data.training_days_of_week || []);
                const formattedRoutines = data.routines.map(routine => ({
                    ...routine,
                    exercise_groups: routine.exercise_groups.map(group => ({
                        ...group,
                        exercises: group.exercises.map(ex => ({ ...ex, name: ex.exercise.name }))
                    }))
                })).sort((a, b) => a.sequence_order - b.sequence_order); // Asegurarse de que las rutinas se cargan en orden
                setRoutines(formattedRoutines);
                setExpandedRoutine(0);
                const looksCalendar = formattedRoutines.length > 0 && formattedRoutines.every(r => DAY_LABELS.includes((r.name || '').trim()));
                setTrainingMode(looksCalendar ? 'calendar' : 'sequence');
            } catch (err) {
                setError("No se pudo cargar el programa. Puede que no te pertenezca.");
            } finally {
                setLoading(false);
            }
        };
        fetchProgram();
    }, [programId]);

    // --- MANEJADORES DE ESTADO ACTUALIZADOS ---
    const handleAddRoutine = () => {
        const newOrder = routines.length > 0 ? Math.max(...routines.map(r => r.sequence_order)) + 1 : 1;
        const defaultName = trainingMode === 'calendar'
            ? getCalendarName(newOrder, trainingDays)
            : getAlphaDay(newOrder);
        const newRoutines = [...routines, { name: defaultName, sequence_order: newOrder, exercise_groups: [] }];
        setRoutines(newRoutines);
        setExpandedRoutine(newRoutines.length - 1);
    };
    const handleRemoveRoutine = (index) => {
        const nextRoutines = routines.filter((_, i) => i !== index);
        setRoutines(nextRoutines);
        if (nextRoutines.length === 0) {
            setExpandedRoutine(0);
            return;
        }
        if (expandedRoutine === index) {
            setExpandedRoutine(Math.max(0, index - 1));
        } else if (expandedRoutine > index) {
            setExpandedRoutine(expandedRoutine - 1);
        }
    };
    const handleRoutineChange = (index, field, value) => {
        const newRoutines = [...routines];
        newRoutines[index][field] = value;
        setRoutines(newRoutines);
    };
    const handleDayToggle = (dayNumber) => {
        setTrainingDays(prevDays => {
            if (prevDays.includes(dayNumber)) {
                return prevDays.filter(d => d !== dayNumber);
            } else {
                return [...prevDays, dayNumber].sort((a, b) => a - b);
            }
        });
    };
    const handleApplyModeNames = () => {
        const renamed = routines.map((routine, i) => ({
            ...routine,
            name: trainingMode === 'calendar'
                ? getCalendarName(i + 1, trainingDays)
                : getAlphaDay(i + 1),
        }));
        setRoutines(renamed);
        toast.success('Nombres de rutinas actualizados según el modo.');
    };
    const handleAddGroup = (routineIndex) => {
        const newRoutines = [...routines];
        newRoutines[routineIndex].exercise_groups.push({
            group_type: 'simple', sets: 3, rest_between_exercises_seconds: 0,
            rest_between_sets_seconds: 60, exercises: []
        });
        setRoutines(newRoutines);
    };
    const handleRemoveGroup = (routineIndex, groupIndex) => {
        const newRoutines = [...routines];
        newRoutines[routineIndex].exercise_groups.splice(groupIndex, 1);
        setRoutines(newRoutines);
    };
    const handleGroupChange = (routineIndex, groupIndex, field, value) => {
        const newRoutines = [...routines];
        newRoutines[routineIndex].exercise_groups[groupIndex][field] = value;
        setRoutines(newRoutines);
    };
    const openExercisePicker = (routineIndex, groupIndex) => {
        setCurrentRoutineIndex(routineIndex);
        setCurrentGroupIndex(groupIndex);
        setIsPickerOpen(true);
    };
    const handleSelectExercise = (exercise) => {
        const newRoutines = [...routines];
        const targetGroup = newRoutines[currentRoutineIndex].exercise_groups[currentGroupIndex];
        targetGroup.exercises.push({
            exercise_id: exercise.id, name: exercise.name, reps: '10-12', weight_kg: '',
            display_order: targetGroup.exercises.length + 1, technique_notes: ''
        });
        setRoutines(newRoutines);
        setIsPickerOpen(false);
    };
    const handleExerciseChange = (routineIndex, groupIndex, exerciseIndex, field, value) => {
        const newRoutines = [...routines];
        newRoutines[routineIndex].exercise_groups[groupIndex].exercises[exerciseIndex][field] = value;
        setRoutines(newRoutines);
    };
    const handleRemoveExercise = (routineIndex, groupIndex, exerciseIndex) => {
        const newRoutines = [...routines];
        newRoutines[routineIndex].exercise_groups[groupIndex].exercises.splice(exerciseIndex, 1);
        setRoutines(newRoutines);
    };

    // --- FUNCIÓN DE ENVÍO ACTUALIZADA (usa PUT) ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        if (trainingMode === 'calendar' && trainingDays.length === 0) {
            setLoading(false);
            toast.error('Selecciona al menos un día si usas modo calendario.');
            return;
        }
        const cleanedRoutines = routines.map((routine, index) => ({
            name: routine.name,
            sequence_order: index + 1,
            exercise_groups: routine.exercise_groups.map(group => ({
                group_type: group.group_type,
                sets: parseInt(group.sets, 10) || 3,
                rest_between_exercises_seconds: parseInt(group.rest_between_exercises_seconds, 10) || 0,
                rest_between_sets_seconds: parseInt(group.rest_between_sets_seconds, 10) || 60,
                exercises: group.exercises.map(ex => ({
                    exercise_id: ex.exercise_id,
                    reps: ex.reps,
                    weight_kg: ex.weight_kg || null,
                    display_order: ex.display_order,
                    technique_notes: ex.technique_notes || null
                }))
            }))
        }));
        const updatedProgramData = {
            name: programName,
            description,
            duration_weeks: durationWeeks,
            training_days_of_week: trainingDays,
            routines: cleanedRoutines
        };
        try {
            await api.put(`/programs/${programId}`, updatedProgramData);
            toast.success('Programa actualizado con exito.');
            navigate('/programs');
        } catch (err) {
            console.error("Error al actualizar el programa:", err.response?.data);
            let errorMessage = 'Error al actualizar el programa.';
            if (err.response?.data?.detail) {
                if (Array.isArray(err.response.data.detail)) {
                    errorMessage = err.response.data.detail.map(d => `${d.loc.join(' -> ')}: ${d.msg}`).join('; ');
                } else {
                    errorMessage = err.response.data.detail;
                }
            }
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <p className="text-white text-center py-10">Cargando programa...</p>;
    if (error) return <p className="text-red-500 text-center py-10">{error}</p>;

    const selectedRoutine = routines[expandedRoutine];

    return (
        <div className="w-full h-full container mx-auto p-4 md:p-8 text-gray-100 animate-fade-up">
            {isPickerOpen && <ExercisePicker onSelectExercise={handleSelectExercise} onClose={() => setIsPickerOpen(false)} />}
            <form onSubmit={handleSubmit} className="max-w-6xl mx-auto">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <button type="button" onClick={() => navigate(-1)} className="flex items-center text-gray-400 hover:text-white mb-2 transition-colors">
                            <IoArrowBack className="mr-1" /> Volver
                        </button>
                        <h1 className="text-3xl md:text-4xl font-extrabold text-white">Editar Programa</h1>
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        className="bg-marizan-red hover:bg-red-700 text-white px-6 py-3 rounded-xl shadow-lg flex items-center justify-center transition-all transform hover:scale-105 active:scale-95 disabled:opacity-50"
                    >
                        <IoSaveOutline className="mr-2" size={20} />
                        {loading ? 'Guardando...' : 'Actualizar Programa'}
                    </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-1 space-y-6">
                        <div className="glass-panel p-6 rounded-2xl border border-white/10">
                            <h2 className="text-lg font-semibold mb-4 text-white">Información General</h2>
                            <div className="space-y-4">
                                <div>
                                    <label htmlFor="programName" className="block text-sm font-medium text-gray-300 mb-1">Nombre del Programa</label>
                                    <input type="text" id="programName" value={programName} onChange={(e) => setProgramName(e.target.value)} required className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:ring-2 focus:ring-marizan-red outline-none" />
                                </div>
                                <div>
                                    <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-1">Descripción</label>
                                    <textarea id="description" rows="3" value={description} onChange={(e) => setDescription(e.target.value)} className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:ring-2 focus:ring-marizan-red outline-none" />
                                </div>
                                <div>
                                    <label htmlFor="durationWeeks" className="block text-sm font-medium text-gray-300 mb-1">Duración (Semanas)</label>
                                    <input type="number" id="durationWeeks" value={durationWeeks} onChange={(e) => setDurationWeeks(parseInt(e.target.value, 10) || 1)} min="1" required className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:ring-2 focus:ring-marizan-red outline-none" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Modo de rutina</label>
                                    <div className="grid grid-cols-2 gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setTrainingMode('sequence')}
                                            className={`px-3 py-2 rounded-lg border text-sm font-semibold transition-colors ${trainingMode === 'sequence' ? 'bg-marizan-red/15 border-marizan-red/40 text-marizan-red' : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'}`}
                                        >
                                            Secuencial (A/B/C)
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setTrainingMode('calendar')}
                                            className={`px-3 py-2 rounded-lg border text-sm font-semibold transition-colors ${trainingMode === 'calendar' ? 'bg-marizan-red/15 border-marizan-red/40 text-marizan-red' : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'}`}
                                        >
                                            Calendario (Lun/Mar)
                                        </button>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleApplyModeNames}
                                        className="mt-2 text-xs text-gray-400 hover:text-white underline"
                                    >
                                        Auto-renombrar rutinas según modo
                                    </button>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Días de Entrenamiento</label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {DAY_LABELS.map((day, index) => (
                                            <label key={day} className="flex items-center gap-2 text-sm text-gray-200 bg-white/5 rounded-lg px-2.5 py-2 border border-white/10">
                                                <input type="checkbox" checked={trainingDays.includes(index + 1)} onChange={() => handleDayToggle(index + 1)} className="h-4 w-4 rounded bg-gray-700 border-gray-600 text-marizan-red focus:ring-marizan-red" />
                                                <span>{day}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="glass-panel p-6 rounded-2xl border border-white/10">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-lg font-semibold text-white">Rutinas</h2>
                                <button type="button" onClick={handleAddRoutine} className="flex items-center bg-marizan-red/20 hover:bg-marizan-red/30 text-marizan-red font-semibold py-1.5 px-3 rounded-lg text-sm transition-colors">
                                    <IoAdd size={16} className="mr-1" /> Añadir
                                </button>
                            </div>
                            <div className="space-y-3">
                                {routines.map((routine, routineIndex) => {
                                    const groups = routine.exercise_groups?.length || 0;
                                    const exCount = (routine.exercise_groups || []).reduce((acc, g) => acc + (g.exercises?.length || 0), 0);
                                    return (
                                        <button
                                            key={routineIndex}
                                            type="button"
                                            onClick={() => setExpandedRoutine(routineIndex)}
                                            className={`w-full text-left p-3 rounded-xl border transition-all ${expandedRoutine === routineIndex ? 'bg-marizan-red/10 border-marizan-red/40' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}
                                        >
                                            <div className="flex justify-between items-start gap-2">
                                                <span className={`font-semibold ${expandedRoutine === routineIndex ? 'text-marizan-red' : 'text-gray-100'}`}>
                                                    {routine.name || `Rutina ${routineIndex + 1}`}
                                                </span>
                                                <span className="text-xs text-gray-400">#{routineIndex + 1}</span>
                                            </div>
                                            <div className="text-xs text-gray-400 mt-1 flex items-center gap-3">
                                                <span>{groups} bloques</span>
                                                <span>{exCount} ejercicios</span>
                                            </div>
                                        </button>
                                    );
                                })}
                                {routines.length === 0 && <p className="text-center text-gray-400 py-2 text-sm">Añade tu primera rutina para empezar.</p>}
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-2">
                        {!selectedRoutine ? (
                            <div className="glass-panel rounded-2xl border border-white/10 p-8 text-center text-gray-400">
                                <IoBarbellOutline size={44} className="mx-auto mb-3 opacity-70" />
                                <p>No hay rutina seleccionada. Añade una rutina para empezar.</p>
                            </div>
                        ) : (
                            <div className="glass-panel p-6 rounded-2xl border border-white/10">
                                <div className="flex justify-between items-center mb-4 gap-3">
                                    <input
                                        type="text"
                                        placeholder={`Nombre de la Rutina ${expandedRoutine + 1}`}
                                        value={selectedRoutine.name}
                                        onChange={(e) => handleRoutineChange(expandedRoutine, 'name', e.target.value)}
                                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-xl font-bold"
                                    />
                                    <button type="button" onClick={() => handleRemoveRoutine(expandedRoutine)} className="text-gray-400 hover:text-red-500 p-2 rounded-lg hover:bg-red-500/10">
                                        <IoTrash size={20} />
                                    </button>
                                </div>

                                <div className="space-y-4 mt-4 border-t border-white/10 pt-4">
                                    {selectedRoutine.exercise_groups.map((group, groupIndex) => (
                                        <div key={groupIndex} className="bg-white/5 p-4 rounded-lg relative border border-white/10">
                                            <button type="button" onClick={() => handleRemoveGroup(expandedRoutine, groupIndex)} className="absolute top-2 right-2 text-gray-500 hover:text-red-400"><IoTrash size={18} /></button>
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                                <select value={group.group_type} onChange={(e) => handleGroupChange(expandedRoutine, groupIndex, 'group_type', e.target.value)} className="bg-gray-800 p-2 rounded text-white border border-white/10"><option value="simple">Serie Simple</option><option value="superset">Super-Set</option><option value="triset">Tri-Set</option><option value="circuit">Circuito</option></select>
                                                <div><label className="text-xs text-gray-400">Sets</label><input type="number" value={group.sets} onChange={(e) => handleGroupChange(expandedRoutine, groupIndex, 'sets', e.target.value)} className="w-full bg-gray-800 p-2 rounded text-white mt-1 border border-white/10" /></div>
                                                <div><label className="text-xs text-gray-400">Desc. entre Ej. (s)</label><input type="number" value={group.rest_between_exercises_seconds || ''} onChange={(e) => handleGroupChange(expandedRoutine, groupIndex, 'rest_between_exercises_seconds', e.target.value)} className="w-full bg-gray-800 p-2 rounded text-white mt-1 border border-white/10" /></div>
                                                <div><label className="text-xs text-gray-400">Desc. entre Sets (s)</label><input type="number" value={group.rest_between_sets_seconds || ''} onChange={(e) => handleGroupChange(expandedRoutine, groupIndex, 'rest_between_sets_seconds', e.target.value)} className="w-full bg-gray-800 p-2 rounded text-white mt-1 border border-white/10" /></div>
                                            </div>
                                            <div className="space-y-2">
                                                {group.exercises.map((exercise, exerciseIndex) => (
                                                    <div key={exerciseIndex} className="grid grid-cols-1 md:grid-cols-12 gap-2 bg-gray-800/90 p-2 rounded items-center border border-white/10">
                                                        <p className="md:col-span-4 text-white font-semibold truncate">{exercise.name}</p>
                                                        <div className="md:col-span-2">
                                                            <label className="md:hidden block text-[11px] text-gray-400 mb-1">Reps</label>
                                                            <input type="text" value={exercise.reps} onChange={(e) => handleExerciseChange(expandedRoutine, groupIndex, exerciseIndex, 'reps', e.target.value)} placeholder="Reps" className="w-full bg-gray-700 p-2 rounded text-white text-center" />
                                                        </div>
                                                        <div className="md:col-span-2">
                                                            <label className="md:hidden block text-[11px] text-gray-400 mb-1">Peso Sugerido</label>
                                                            <input type="text" value={exercise.weight_kg || ''} onChange={(e) => handleExerciseChange(expandedRoutine, groupIndex, exerciseIndex, 'weight_kg', e.target.value)} placeholder="Peso Sug." className="w-full bg-gray-700 p-2 rounded text-white text-center" />
                                                        </div>
                                                        <div className="md:col-span-3">
                                                            <label className="md:hidden block text-[11px] text-gray-400 mb-1">Técnica</label>
                                                            <input type="text" value={exercise.technique_notes || ''} onChange={(e) => handleExerciseChange(expandedRoutine, groupIndex, exerciseIndex, 'technique_notes', e.target.value)} placeholder="Técnica (ej. Drop-Set)" className="w-full bg-gray-700 p-2 rounded text-white" />
                                                        </div>
                                                        <div className="md:col-span-1 flex md:justify-center justify-end">
                                                            <button type="button" onClick={() => handleRemoveExercise(expandedRoutine, groupIndex, exerciseIndex)} className="text-gray-500 hover:text-red-500 p-1"><IoTrash /></button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            <button type="button" onClick={() => openExercisePicker(expandedRoutine, groupIndex)} className="flex items-center text-blue-400 hover:text-blue-300 mt-4 text-sm"><IoAddCircleOutline className="mr-2" /> Añadir Ejercicio al Bloque</button>
                                        </div>
                                    ))}
                                    <button type="button" onClick={() => handleAddGroup(expandedRoutine)} className="w-full text-center bg-white/10 hover:bg-white/15 text-white font-bold py-2 px-4 rounded-lg mt-4 border border-white/10"><IoAdd size={20} className="inline mr-2" />Añadir Bloque (Simple, Superserie, etc.)</button>
                                </div>
                            </div>
                        )}

                        {error && <p className="text-red-400 text-center mt-4">{error}</p>}
                    </div>
                </div>
            </form>
        </div>
    );
};

export default ProgramEdit;